import React from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { Box, Button, Checkbox, Chip, FormControl, InputLabel, Menu, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { color } from '@/styles';
import { IGetUnitRes } from '@/types/api/unit/getUnit';
import { IGetResidentRes } from '@/types/api/resident/getResident';
import { NOTIFICATION_ICON_PATH } from '@/constants/notification';
import { selfDataSelector, showMedicalContentSelector } from '@/redux/config/selfData';
import { useServiceTranslation } from '@/hooks/useServiceTranslation';

import { getNotificationFilterOptions } from '@/constants/notificationFilters';
import { ResidentFilters } from '@/utils/notificationFilters';

interface Props {
  title: string;
  units: IGetUnitRes[];
  residents: IGetResidentRes[];
  filters: ResidentFilters;
  onUpdate: () => void;
  onChangeFilters: (filters: ResidentFilters) => void;
}

const Container = css`
  position: sticky;
  z-index: 2;
  top: 0;
  padding: 16px 0;
  background-color: var(--surface-list-header);
`;

const HeaderContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleContainer = css`
  display: flex;
  align-items: center;
`;

const Title = css`
  font-size: 20px;
  font-weight: bold;
  margin-right: 16px;
`;

const UpdateButton = css`
  border-radius: 100px;
  color: ${color.dark_gray};
  background-color: var(--button-bg);
  &:hover {
    background-color: var(--button-bg);
  }
`;

const RightElementContainer = css`
  display: flex;
`;

const FilterButton = css`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const FilterPanel = css`
  padding: 16px;
  background: ${color.white};
`;

const SelectorContainer = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 12px;
`;

const CheckBoxContainer = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: min(300px, 40vh);
  overflow-y: auto;
`;

const CheckBoxItem = css`
  display: inline-flex;
  align-items: center;
  min-height: 30px;
`;

const CheckBoxLabel = css`
  display: inline-flex;
  align-items: center;
  column-gap: 8px;
  color: ${color.dark_gray};
`;

const NotificationIcon = css`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
`;

export const NotificationResidentListHeader = React.memo(
  ({ title, units, residents, filters, onUpdate, onChangeFilters }: Props) => {
    const { t, serviceT } = useServiceTranslation();
    const { selfUser } = useSelector(selfDataSelector);
    const showMedicalContent = useSelector(showMedicalContentSelector);
    const [anchorElFilter, setAnchorElFilter] = React.useState<null | HTMLElement>(null);
    const residentLabel = showMedicalContent ? t('resident_medical', '利用者') : t('resident', '入居者');

    const notificationTypeOptions = getNotificationFilterOptions(showMedicalContent);
    const categoryOptions = React.useMemo(
      () => [
        { value: 'deviceAssigned', label: t('DFreeIsAssigned', 'デバイス登録中') },
        { value: 'deviceAttached', label: t('DFreeIsAttached', '測定中') },
        ...(!selfUser.hasUnitPerm
          ? units.map((unit) => ({ value: `unit-${unit.id}`, label: unit.name }))
          : []),
      ],
      [selfUser.hasUnitPerm, t, units],
    );

    const sortedResidents = React.useMemo(() => {
      return [...residents].sort((a, b) => {
        const nameA = `${a.lastName ?? ''}${a.name ?? ''}`;
        const nameB = `${b.lastName ?? ''}${b.name ?? ''}`;
        return nameA.localeCompare(nameB, 'ja');
      });
    }, [residents]);

    const handleChangeCategory = (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value;
      onChangeFilters({ ...filters, categories: typeof value === 'string' ? value.split(',') : value });
    };

    const handleRemoveCategory = (value: string) => {
      onChangeFilters({ ...filters, categories: filters.categories.filter((category) => category !== value) });
    };

    const handleChangeResident = (event: SelectChangeEvent<string>) => {
      onChangeFilters({ ...filters, residentId: event.target.value === '' ? null : +event.target.value });
    };

    const handleToggleNotificationType = (notificationType: string) => () => {
      const notificationTypes = filters.notificationTypes.includes(notificationType)
        ? filters.notificationTypes.filter((type) => type !== notificationType)
        : [...filters.notificationTypes, notificationType];
      onChangeFilters({ ...filters, notificationTypes });
    };

    const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorElFilter(event.currentTarget);
    };

    const handleCloseFilter = () => {
      setAnchorElFilter(null);
    };

    const isFilterActive =
      filters.categories.length > 0 ||
      filters.residentId !== null ||
      filters.notificationTypes.length > 0;

    return (
      <div css={Container}>
        <div css={HeaderContainer}>
          <div css={TitleContainer}>
            <div css={Title}>{title}</div>
            <Button css={UpdateButton} onClick={onUpdate} startIcon={<RefreshIcon color='primary' />}>
              {t('update', '更新')}
            </Button>
          </div>
          <div css={RightElementContainer}>
            <Button onClick={handleFilterClick} css={FilterButton}>
              <span style={{ color: color.black }}>{t('filter', '絞り込み')}</span>
              <>{isFilterActive ? <FilterAltIcon /> : <ArrowDropDownIcon />}</>
            </Button>
          </div>
        </div>
        <Menu
          open={!!anchorElFilter}
          anchorEl={anchorElFilter}
          onClose={handleCloseFilter}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 360, maxWidth: 'calc(100vw - 32px)', maxHeight: 'min(560px, 70vh)', overflowY: 'auto' } }}
        >
          <div css={FilterPanel}>
            <div css={SelectorContainer}>
              <FormControl fullWidth size='small'>
                <InputLabel id='notification-category-filter-label'>{t('category', 'カテゴリー')}</InputLabel>
                <Select
                  labelId='notification-category-filter-label'
                  multiple
                  value={filters.categories}
                  label={t('category', 'カテゴリー')}
                  onChange={handleChangeCategory}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={categoryOptions.find((option) => option.value === value)?.label ?? value}
                          size='small'
                          onMouseDown={(event) => event.stopPropagation()}
                          onDelete={() => handleRemoveCategory(value)}
                          sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 'min(320px, 60vh)', overflowY: 'auto' } } }}
                >
                  {categoryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ py: 0.25, minHeight: 36, whiteSpace: 'normal' }}>
                      <Checkbox checked={filters.categories.includes(option.value)} size='small' sx={{ p: 0.5, mr: 0.5 }} />
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size='small'>
                <InputLabel id='notification-resident-filter-label'>{residentLabel}</InputLabel>
                <Select
                  labelId='notification-resident-filter-label'
                  value={filters.residentId ? String(filters.residentId) : ''}
                  label={residentLabel}
                  onChange={handleChangeResident}
                >
                  <MenuItem value=''>{t('notSelected', '未選択')}</MenuItem>
                  {sortedResidents.map((resident) => (
                    <MenuItem key={resident.id} value={String(resident.id)}>
                      {resident.lastName} {resident.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ color: color.gray, marginBottom: 4 }}>{t('notification', '通知')}</div>
            <div css={CheckBoxContainer}>
              {notificationTypeOptions.map((option) => (
                <label key={option.value} css={CheckBoxItem}>
                  <Checkbox
                    color='primary'
                    size='small'
                    checked={filters.notificationTypes.includes(option.value)}
                    onChange={handleToggleNotificationType(option.value)}
                    sx={{ p: 0.5, mr: 0.5 }}
                  />
                  <span css={CheckBoxLabel}>
                    {option.iconType && (
                      <img
                        css={NotificationIcon}
                        src={NOTIFICATION_ICON_PATH[option.iconType] ?? NOTIFICATION_ICON_PATH.default}
                        alt=''
                      />
                    )}
                    {serviceT(option.labelKey)}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </Menu>
      </div>
    );
  },
);
